import React, { useState, useCallback, useMemo, useEffect, useRef } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Chip,
  TextField,
  Avatar,
  Divider,
  Paper,
  InputAdornment,
} from "@material-ui/core"
import {
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DevicesOther as DevicesOtherIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Error as ErrorIcon,
  Router as RouterIcon,
  BrokenImage as BrokenImageIcon,
  ZoomIn as ZoomInIcon,
  CreditCard as CreditCardIcon,
  GetApp as GetAppIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from "@material-ui/icons"
import moment from "moment"
import "moment/locale/es"
import { useSelector } from "react-redux"
import axiosInstance from "src/utils/axios"
import { selectConjunto } from "src/redux/slices/complexSlice"
import { useSnackbar } from "notistack"
import useStyles from "./BiometricLogsDialog.styles"
import { exportLogsToExcel } from "./ExportLogsToExcel"

moment.locale("es")

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50]
const DEFAULT_ROWS = 20
const FETCH_ALL_BATCH = 50
const EVENT_FILTER_OPTIONS = [
  { value: "facial_ok", label: "Rostros autorizados", minors: 75 },
  { value: "facial_fail", label: "Rostros no autorizados", minors: 76 },
  { value: "tarjeta", label: "Tarjetas", minors: 1 },
  { value: "qr", label: "QR / Contraseña", minors: 24 },
]

const MINOR_LABELS = {
  1: "Tarjeta válida",
  2: "Tarjeta inválida",
  3: "Tarjeta caducada",
  4: "Sin autorización de horario",
  5: "Acceso denegado",
  8: "Huella dactilar",
  22: "Contraseña",
  24: "Código QR",
  37: "Contraseña incorrecta",
  38: "Reconocimiento facial — OK",
  39: "Reconocimiento facial — Fallido",
  43: "Tarjeta + Rostro",
  60: "Puerta abierta",
  61: "Puerta cerrada",
  62: "Alarma puerta abierta",
  63: "Apertura normal",
  75: "Reconocimiento facial",
  76: "Reconocimiento facial — Umbral bajo",
}

const getMinorLabel = c => MINOR_LABELS[c] ?? `Sub-evento ${c}`
const MAX_RANGE_DAYS = 3

function getAccessResult(item) {
  if (item.major !== 5) return "info"
  const OK = [1, 8, 22, 24, 38, 43, 75]
  const FAIL = [2, 3, 4, 5, 37, 39, 76]
  if (OK.includes(item.minor)) return "success"
  if (FAIL.includes(item.minor)) return "error"
  return "info"
}

const HEX_RE = /^[0-9a-f]{40,}$/i
const isHashed = value => typeof value === "string" && HEX_RE.test(value.trim())

const resolveName = item => {
  const raw = item.name
  if (!raw || isHashed(raw)) return null
  return raw
}

const resolveCardId = item => {
  const raw = item.employeeNoString || item.cardNo
  if (!raw) return null
  if (isHashed(raw))
    return { value: raw.slice(0, 8).toUpperCase(), isHash: true }
  return { value: raw, isHash: false }
}

const EventPhoto = ({ pictureURL, conjuntoId, onClick, photoCache }) => {
  const [blobUrl, setBlobUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const blobRef = useRef(null)

  useEffect(() => {
    if (!pictureURL || !conjuntoId) return

    if (photoCache.current[pictureURL]) {
      const blob = new Blob([photoCache.current[pictureURL]], {
        type: "image/jpeg",
      })
      const url = URL.createObjectURL(blob)
      blobRef.current = url
      setBlobUrl(url)
      return
    }

    setLoading(true)
    setHasError(false)

    let cancelled = false
    const interval = setInterval(() => {
      if (cancelled) {
        clearInterval(interval)
        return
      }
      if (photoCache.current[pictureURL] === "error") {
        clearInterval(interval)
        setHasError(true)
        setLoading(false)
        return
      }
      if (photoCache.current[pictureURL]) {
        clearInterval(interval)
        const blob = new Blob([photoCache.current[pictureURL]], {
          type: "image/jpeg",
        })
        const url = URL.createObjectURL(blob)
        blobRef.current = url
        setBlobUrl(url)
        setLoading(false)
      }
    }, 300)

    return () => {
      cancelled = true
      clearInterval(interval)
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current)
        blobRef.current = null
      }
    }
  }, [pictureURL, conjuntoId, photoCache])

  if (!pictureURL) {
    return (
      <Box
        style={{
          width: 52,
          height: 52,
          borderRadius: 8,
          backgroundColor: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PersonIcon style={{ fontSize: 22, color: "#cbd5e1" }} />
      </Box>
    )
  }
  if (loading) {
    return (
      <Box
        style={{
          width: 52,
          height: 52,
          borderRadius: 8,
          backgroundColor: "#e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={18} style={{ color: "#94a3b8" }} />
      </Box>
    )
  }
  if (hasError || !blobUrl) {
    return (
      <Box
        style={{
          width: 52,
          height: 52,
          borderRadius: 8,
          backgroundColor: "#fef2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <BrokenImageIcon style={{ fontSize: 20, color: "#fca5a5" }} />
      </Box>
    )
  }
  return (
    <Box
      onClick={() => onClick(blobUrl)}
      style={{
        position: "relative",
        width: 52,
        height: 52,
        cursor: "pointer",
        borderRadius: 8,
        overflow: "hidden",
        border: "2px solid #e2e8f0",
      }}
    >
      <img
        src={blobUrl}
        alt="Captura evento"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
      <Box
        className="photo-overlay"
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0,
          transition: "opacity 0.15s",
        }}
      >
        <ZoomInIcon style={{ color: "#fff", fontSize: 22 }} />
      </Box>
    </Box>
  )
}

const PhotoLightbox = ({ src, onClose }) => (
  <Dialog open={Boolean(src)} onClose={onClose} maxWidth="md">
    <DialogTitle disableTypography>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Captura del evento
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent
      style={{ padding: 8, textAlign: "center", background: "#0f172a" }}
    >
      {src && (
        <img
          src={src}
          alt="Captura evento"
          style={{
            maxWidth: "100%",
            maxHeight: "75vh",
            borderRadius: 8,
            objectFit: "contain",
          }}
        />
      )}
    </DialogContent>
  </Dialog>
)

const BiometricLogsDialog = ({ open, onClose, device }) => {
  const classes = useStyles()
  const conjunto = useSelector(selectConjunto)
  const [eventFilter, setEventFilter] = useState("facial_ok")

  const [dateTimeFrom, setDateTimeFrom] = useState(
    moment().startOf("day").format("YYYY-MM-DDTHH:mm")
  )
  const [dateTimeTo, setDateTimeTo] = useState(
    moment().format("YYYY-MM-DDTHH:mm")
  )
  const [searchQuery, setSearchQuery] = useState("")

  const [allLogs, setAllLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasFetched, setHasFetched] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS)
  const [lightboxSrc, setLightboxSrc] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [loadAllProgress, setLoadAllProgress] = useState(0)

  const photoCache = useRef({})
  const throttlerActive = useRef(false)

  const { enqueueSnackbar } = useSnackbar()
  const toIso = useCallback(localStr => moment(localStr).toISOString(), [])

  const startImageThrottler = useCallback(
    async (logsToLoad, conjuntoId, signal) => {
      const BATCH_SIZE = 3
      const BATCH_DELAY = 300

      const urls = logsToLoad
        .map(item => item.pictureURL)
        .filter(url => url && !photoCache.current[url])

      const fetchOne = async url => {
        if (signal.aborted) return
        try {
          const proxyUrl =
            `api/biometrics/conjuntos/${conjuntoId}/biometricos/image-proxy/` +
            `?url=${encodeURIComponent(url)}`
          const response = await axiosInstance.get(proxyUrl, {
            responseType: "arraybuffer",
          })
          if (!signal.aborted) photoCache.current[url] = response.data
        } catch {
          if (!signal.aborted) photoCache.current[url] = "error"
        }
      }

      const processBatch = async startIndex => {
        if (signal.aborted || startIndex >= urls.length) return
        const batch = urls.slice(startIndex, startIndex + BATCH_SIZE)
        await Promise.all(batch.map(fetchOne))
        if (!signal.aborted) {
          await new Promise(r => {
            setTimeout(r, BATCH_DELAY)
          })
          processBatch(startIndex + BATCH_SIZE)
        }
      }

      processBatch(0)
    },
    []
  )

  const fetchAllLogs = useCallback(
    async (fromStr, toStr, onProgress) => {
      if (!device || !conjunto?.id) return []

      const fetchPage = async pos => {
        const filterOpt =
          EVENT_FILTER_OPTIONS.find(o => o.value === eventFilter) ??
          EVENT_FILTER_OPTIONS[0]

        const { data } = await axiosInstance.post(
          `api/biometrics/conjuntos/${conjunto.id}/biometricos/acs-events/`,
          {
            ip: device.ip,
            startTime: toIso(fromStr),
            endTime: toIso(toStr),
            searchResultPosition: pos,
            maxResults: FETCH_ALL_BATCH,
            minor: filterOpt.minors,
            major: 5,
          }
        )
        if (!data.success)
          throw new Error(data.error || "Error del dispositivo")
        return {
          items: data.data?.AcsEvent?.InfoList ?? [],
          total: data.data?.AcsEvent?.totalMatches ?? 0,
        }
      }

      const fetchFrom = async (pos, collected, total) => {
        if (pos >= total) return collected
        await new Promise(r => {
          setTimeout(r, 200)
        })
        const result = await fetchPage(pos)
        const updated = [...collected, ...result.items]
        if (onProgress) onProgress(Math.round((updated.length / total) * 100))
        return fetchFrom(pos + FETCH_ALL_BATCH, updated, total)
      }

      const first = await fetchPage(0)
      const { total } = first
      const initial = [...first.items]
      if (onProgress)
        onProgress(Math.round((initial.length / Math.max(total, 1)) * 100))

      return fetchFrom(FETCH_ALL_BATCH, initial, total)
    },
    [device, conjunto?.id, toIso, eventFilter]
  )

  const fetchLogs = useCallback(
    async (fromStr, toStr) => {
      if (!device || !conjunto?.id) return
      setLoading(true)
      setError(null)
      setPage(0)
      setSearchQuery("")
      setLoadAllProgress(0)
      photoCache.current = {}
      throttlerActive.current = false

      try {
        const all = await fetchAllLogs(fromStr, toStr, pct =>
          setLoadAllProgress(pct)
        )
        const withPhoto = all.filter(e => e.pictureURL)
        setAllLogs(withPhoto)
        setHasFetched(true)
      } catch (err) {
        setError(
          err?.response?.data?.error || err?.message || "Error de conexión"
        )
        setHasFetched(true)
      } finally {
        setLoading(false)
        setLoadAllProgress(0)
      }
    },
    [device, conjunto?.id, fetchAllLogs, startImageThrottler]
  )

  const filteredLogs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return allLogs
    return allLogs.filter(item => {
      const name = (item.name || "").toLowerCase()
      const empNo = (item.employeeNoString || "").toLowerCase()
      const cardNo = (item.cardNo || "").toLowerCase()
      return name.includes(q) || empNo.includes(q) || cardNo.includes(q)
    })
  }, [allLogs, searchQuery])

  const displayedLogs = useMemo(
    () =>
      filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredLogs, page, rowsPerPage]
  )

  useEffect(() => {
    if (!conjunto?.id || displayedLogs.length === 0) return undefined
    const controller = new AbortController()
    startImageThrottler(displayedLogs, conjunto.id, controller.signal)
    return () => {
      controller.abort()
    }
  }, [displayedLogs, conjunto?.id, startImageThrottler])

  const handleChangePage = useCallback((_, newPage) => setPage(newPage), [])
  const handleChangeRowsPerPage = useCallback(e => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setPage(0)
  }, [])
  const handleSearchChange = useCallback(value => {
    setSearchQuery(value)
    setPage(0)
  }, [])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      await exportLogsToExcel({
        logs: displayedLogs,
        device,
        conjuntoId: conjunto.id,
        dateTimeFrom,
        dateTimeTo,
        searchQuery,
        photoCache: photoCache.current,
      })
    } catch {
      enqueueSnackbar("Error al exportar Excel", { variant: "error" })
    } finally {
      setExporting(false)
    }
  }, [
    displayedLogs,
    device,
    conjunto?.id,
    dateTimeFrom,
    dateTimeTo,
    searchQuery,
  ])

  const handleClose = useCallback(() => {
    setAllLogs([])
    setError(null)
    setHasFetched(false)
    setPage(0)
    setLightboxSrc(null)
    setSearchQuery("")
    setLoadAllProgress(0)
    photoCache.current = {}
    throttlerActive.current = false
    onClose()
  }, [onClose])

  const stats = useMemo(
    () => ({
      ok: filteredLogs.filter(l => getAccessResult(l) === "success").length,
      fail: filteredLogs.filter(l => getAccessResult(l) === "error").length,
    }),
    [filteredLogs]
  )

  const rangeLabel = useMemo(() => {
    const from = moment(dateTimeFrom).format("DD/MM/YY HH:mm")
    const to = moment(dateTimeTo).format("DD/MM/YY HH:mm")
    return `${from} — ${to}`
  }, [dateTimeFrom, dateTimeTo])

  if (!device) return null

  const totalMatches = filteredLogs.length

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Box className={classes.titleRow}>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <EventNoteIcon style={{ fontSize: 26 }} />
              <Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  Registros de Acceso
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8, marginTop: 4 }}
                >
                  <Chip
                    size="small"
                    icon={
                      <RouterIcon style={{ fontSize: 13, color: "#fff" }} />
                    }
                    label={device.nombre}
                    className={classes.deviceChipHeader}
                  />
                  <Typography variant="caption" style={{ opacity: 0.75 }}>
                    {device.ip} · {device.ubicacion}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              className={classes.iconBtn}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box className={classes.filterBar}>
          <Box className={classes.filterRow}>
            <TextField
              label="Desde"
              type="datetime-local"
              value={dateTimeFrom}
              onChange={e => {
                setDateTimeFrom(e.target.value)
                setHasFetched(false)
                setAllLogs([])
                setSearchQuery("")
                photoCache.current = {}
              }}
              variant="outlined"
              size="small"
              className={classes.dateField}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: moment()
                  .subtract(1, "month")
                  .startOf("day")
                  .format("YYYY-MM-DDTHH:mm"),
                max: dateTimeTo,
              }}
            />

            <TextField
              label="Hasta"
              type="datetime-local"
              value={dateTimeTo}
              onChange={e => {
                setDateTimeTo(e.target.value)
                setHasFetched(false)
                setAllLogs([])
                setSearchQuery("")
                photoCache.current = {}
                throttlerActive.current = false
              }}
              variant="outlined"
              size="small"
              className={classes.dateField}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: moment()
                  .subtract(1, "month")
                  .startOf("day")
                  .format("YYYY-MM-DDTHH:mm"),
                max: moment().format("YYYY-MM-DDTHH:mm"),
              }}
            />

            <TextField
              select
              label="Tipo de evento"
              value={eventFilter}
              onChange={e => setEventFilter(e.target.value)}
              variant="outlined"
              size="small"
              style={{ minWidth: 200 }}
              SelectProps={{ native: true }}
            >
              {EVENT_FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </TextField>

            <Button
              variant="contained"
              color="primary"
              startIcon={
                loading ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              onClick={() => {
                const diffDays = moment(dateTimeTo).diff(
                  moment(dateTimeFrom),
                  "days"
                )
                if (diffDays > MAX_RANGE_DAYS) {
                  enqueueSnackbar(
                    `El rango máximo de consulta es de ${MAX_RANGE_DAYS} días.`,
                    { variant: "error" }
                  )
                  return
                }
                fetchLogs(dateTimeFrom, dateTimeTo)
              }}
              disabled={loading}
              className={classes.searchBtn}
            >
              {(() => {
                if (loading && loadAllProgress > 0)
                  return `Cargando ${loadAllProgress}%...`
                if (loading) return "Consultando..."
                return "Buscar"
              })()}
            </Button>

            {hasFetched && !loading && (
              <Tooltip title="Actualizar">
                <IconButton
                  size="small"
                  onClick={() => fetchLogs(dateTimeFrom, dateTimeTo)}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {hasFetched && !error && allLogs.length > 0 && (
            <Box className={classes.searchRow}>
              <TextField
                placeholder="Buscar por nombre o cédula..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon
                        style={{ fontSize: 18, color: "#94a3b8" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleSearchChange("")}
                      >
                        <ClearIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {searchQuery && (
                <Typography
                  variant="caption"
                  style={{
                    color: "#64748b",
                    alignSelf: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {filteredLogs.length} de {allLogs.length} resultado
                  {filteredLogs.length !== 1 ? "s" : ""}
                </Typography>
              )}

              <Tooltip
                title={`Exportar ${totalMatches} registro(s) a Excel con fotos`}
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      exporting ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <GetAppIcon style={{ fontSize: 16 }} />
                      )
                    }
                    onClick={handleExport}
                    disabled={exporting || allLogs.length === 0}
                    className={classes.exportBtn}
                  >
                    {exporting
                      ? "Generando Excel..."
                      : `Exportar página (${displayedLogs.length})`}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {hasFetched && !error && allLogs.length > 0 && (
          <Box className={classes.statsRow}>
            <AccessTimeIcon style={{ fontSize: 15, color: "#64748b" }} />
            <Typography
              variant="caption"
              style={{ color: "#64748b", fontWeight: 600 }}
            >
              {rangeLabel}
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Chip
              size="small"
              icon={
                <CheckCircleIcon style={{ fontSize: 13, color: "#166534" }} />
              }
              label={`${stats.ok} permitidos`}
              style={{
                backgroundColor: "#dcfce7",
                color: "#166534",
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            <Chip
              size="small"
              icon={<CancelIcon style={{ fontSize: 13, color: "#991b1b" }} />}
              label={`${stats.fail} denegados`}
              style={{
                backgroundColor: "#fee2e2",
                color: "#991b1b",
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            <Chip
              size="small"
              label={`${totalMatches} total`}
              style={{
                backgroundColor: "#f1f5f9",
                color: "#475569",
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
          </Box>
        )}

        <DialogContent style={{ padding: 0, minHeight: 320 }}>
          {loading && (
            <Box className={classes.loadingBox}>
              <CircularProgress size={46} />
              <Typography variant="body2" style={{ color: "#64748b" }}>
                {loadAllProgress > 0
                  ? `Obteniendo registros... ${loadAllProgress}%`
                  : `Consultando ${device.ip}...`}
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Box className={classes.errorBox}>
              <Box display="flex" alignItems="flex-start" style={{ gap: 10 }}>
                <ErrorIcon
                  style={{ color: "#dc2626", flexShrink: 0, marginTop: 2 }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 700, marginBottom: 6 }}
                  >
                    Error al obtener registros
                  </Typography>
                  {error.split("\n").map(line => (
                    <Typography
                      key={line}
                      variant="body2"
                      style={{ lineHeight: 1.9 }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {!loading && !error && hasFetched && displayedLogs.length === 0 && (
            <Box className={classes.emptyBox}>
              <DevicesOtherIcon
                style={{ fontSize: 56, color: "#cbd5e0", marginBottom: 12 }}
              />
              <Typography
                variant="h6"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                {searchQuery
                  ? "Sin coincidencias"
                  : "Sin registros con foto para este rango"}
              </Typography>
              <Typography variant="body2" style={{ color: "#94a3b8" }}>
                {searchQuery
                  ? `No se encontraron registros para "${searchQuery}"`
                  : `No se encontraron eventos con foto entre ${rangeLabel} en ${device.nombre}`}
              </Typography>
            </Box>
          )}

          {!loading && !error && displayedLogs.length > 0 && (
            <TableContainer
              component={Paper}
              elevation={0}
              className={classes.tableContainer}
            >
              <Table size="small" stickyHeader>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Foto</TableCell>
                    <TableCell>Fecha / Hora</TableCell>
                    <TableCell>Persona</TableCell>
                    <TableCell>Cédula / ID</TableCell>
                    <TableCell>Detalle</TableCell>
                    <TableCell align="center">Puerta</TableCell>
                    <TableCell>Resultado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedLogs.map((item, idx) => {
                    const result = getAccessResult(item)
                    const rowNum = page * rowsPerPage + idx + 1
                    const timeStr = item.time
                      ? moment(item.time).format("DD/MM/YY HH:mm:ss")
                      : "—"
                    const name = resolveName(item)
                    const cardInfo = resolveCardId(item)

                    return (
                      <TableRow
                        key={
                          item.time +
                          (item.employeeNoString || item.cardNo || idx)
                        }
                        className={`${classes.tableRow} event-row`}
                      >
                        <TableCell
                          style={{
                            color: "#94a3b8",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                          }}
                        >
                          {rowNum}
                        </TableCell>

                        <TableCell style={{ padding: "6px 8px" }}>
                          <EventPhoto
                            pictureURL={item.pictureURL}
                            conjuntoId={conjunto.id}
                            onClick={src => setLightboxSrc(src)}
                            photoCache={photoCache}
                          />
                        </TableCell>

                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 5 }}
                          >
                            <AccessTimeIcon
                              style={{ fontSize: 13, color: "#94a3b8" }}
                            />
                            <Typography variant="caption">{timeStr}</Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className={classes.portraitCell}>
                            <Avatar className={classes.avatarSmall}>
                              <PersonIcon style={{ fontSize: 15 }} />
                            </Avatar>
                            {name ? (
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                              >
                                {name}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                style={{
                                  color: "#94a3b8",
                                  fontStyle: "italic",
                                }}
                              >
                                Sin nombre
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>
                          {cardInfo ? (
                            <Tooltip
                              title={
                                cardInfo.isHash
                                  ? "ID cifrado por el dispositivo"
                                  : cardInfo.value
                              }
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 4 }}
                              >
                                <CreditCardIcon
                                  style={{ fontSize: 13, color: "#94a3b8" }}
                                />
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontFamily: "monospace",
                                    color: cardInfo.isHash
                                      ? "#94a3b8"
                                      : "#475569",
                                    fontStyle: cardInfo.isHash
                                      ? "italic"
                                      : "normal",
                                  }}
                                >
                                  {cardInfo.isHash
                                    ? `${cardInfo.value}…`
                                    : cardInfo.value}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="caption"
                              style={{ color: "#cbd5e1" }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {getMinorLabel(item.minor)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography
                            variant="caption"
                            style={{ color: "#64748b" }}
                          >
                            {item.doorNo ?? "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {result === "success" && (
                            <span className={classes.badgeSuccess}>
                              <CheckCircleIcon
                                style={{
                                  fontSize: 11,
                                  verticalAlign: "middle",
                                  marginRight: 3,
                                }}
                              />
                              Permitido
                            </span>
                          )}
                          {result === "error" && (
                            <span className={classes.badgeError}>
                              <CancelIcon
                                style={{
                                  fontSize: 11,
                                  verticalAlign: "middle",
                                  marginRight: 3,
                                }}
                              />
                              Denegado
                            </span>
                          )}
                          {result === "info" && (
                            <span className={classes.badgeDefault}>Info</span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Box flex={1}>
            {hasFetched && !error && totalMatches > 0 && (
              <TablePagination
                component="div"
                count={totalMatches}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count}`
                }
                className={classes.paginationRoot}
              />
            )}
          </Box>
          <Button
            onClick={handleClose}
            variant="outlined"
            style={{ textTransform: "none", minWidth: 90 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      <style>{`
        .event-row:hover .photo-overlay { opacity: 1 !important; }
      `}</style>
    </>
  )
}

export default BiometricLogsDialog
